import { OptimizationTypes, platforms } from '@app/configurations';
import { camelToSnake } from '@app/core/utilities/string.utilities';
import { CriterionAdditionMatchType } from '@app/criterion-addition/shared/models/criterion-addition.models';
import { ProductAdSyncMode } from '@app/criterion-addition/shared/models/product-ad-sync.models';
import { getOptimizeFilter, multiSelectFilter } from '../utilities/refiners.utilities';
import { ApiCommonRoutes, ApiEndpoint } from './api.models';
import {
  CampaignOptimizationValue,
  CampaignTargetingType,
  CompareValues,
  MetricType,
  SubFilter,
  SubFilterOption,
} from './common.models';
import * as Metrics from './metrics.constants';

export interface IMetricsSubFilter extends SubFilter {
  key: Metrics.AllMetrics;
  columnName: Metrics.AllMetrics;
  endRange?: string;
}

const createCompareMetric = (
  key: Metrics.AllMetrics,
  subType: MetricType,
  optiTypes?: CampaignOptimizationValue[],
): IMetricsSubFilter => ({
  key,
  columnName: key,
  type: 'compare',
  apiRefinerType: 'filter',
  subType,
  currency: null,
  comparator: CompareValues.LE,
  optiTypes,
  value: null,
});

export const campaignMetricsFilters: Record<string, IMetricsSubFilter> = {
  [Metrics.COST]: createCompareMetric(Metrics.COST, MetricType.currency, Object.values(OptimizationTypes)),
  [Metrics.IMPRESSIONS]: createCompareMetric(Metrics.IMPRESSIONS, MetricType.number, Object.values(OptimizationTypes)),
  [Metrics.CLICKS]: createCompareMetric(Metrics.CLICKS, MetricType.number, Object.values(OptimizationTypes)),
  [Metrics.CONVERSIONS]: createCompareMetric(Metrics.CONVERSIONS, MetricType.number, [
    OptimizationTypes.REVENUE,
    OptimizationTypes.PROFIT,
  ]),
  [Metrics.COST_REVENUE_RATIO]: createCompareMetric(Metrics.COST_REVENUE_RATIO, MetricType.percent, [
    OptimizationTypes.REVENUE,
  ]),
  [Metrics.COST_PER_CONVERSION]: createCompareMetric(Metrics.COST_PER_CONVERSION, MetricType.currency, [
    OptimizationTypes.REVENUE,
    OptimizationTypes.PROFIT,
  ]),
  [Metrics.ROAS]: createCompareMetric(Metrics.ROAS, MetricType.number, [OptimizationTypes.REVENUE]),
  [Metrics.RETURN_ON_INVESTMENT]: createCompareMetric(Metrics.RETURN_ON_INVESTMENT, MetricType.number, [
    OptimizationTypes.PROFIT,
  ]),
};

export const MetricsFilters: Record<string, IMetricsSubFilter> = {
  ...campaignMetricsFilters,
  [Metrics.REVENUE]: createCompareMetric(Metrics.REVENUE, MetricType.currency),
  [Metrics.REVENUE_PER_CONVERSION]: createCompareMetric(Metrics.REVENUE_PER_CONVERSION, MetricType.currency),
  [Metrics.CONVERSION_RATE]: createCompareMetric(Metrics.CONVERSION_RATE, MetricType.percent),
  [Metrics.CLICK_THROUGH_RATE]: createCompareMetric(Metrics.CLICK_THROUGH_RATE, MetricType.percent),
};

export const suggestionsFilters = {
  proposalType: multiSelectFilter('proposalType', 'proposal_type', [
    { active: true, key: 'ADDITION' },
    { active: true, key: 'EXCLUSION' },
  ]),
  criterionType: multiSelectFilter('criterionType', 'criterion_type', [
    {
      platform: [platforms.GOOGLE, platforms.AMAZON, platforms.EBAY, platforms.WALMART, platforms.MICROSOFT],
      active: true,
      key: 'KEYWORD',
    },
    { platform: [platforms.GOOGLE], active: true, key: 'MOBILE-APPLICATION' },
    { platform: [platforms.GOOGLE], active: true, key: 'PLACEMENT' },
    { platform: [platforms.AMAZON], active: true, key: 'PAT' },
    { platform: [platforms.GOOGLE], active: true, key: 'YOUTUBE-CHANNEL' },
    { platform: [platforms.GOOGLE], active: true, key: 'YOUTUBE-VIDEO' },
  ]),
  proposalReasons: multiSelectFilter('reasons', 'proposal_reasons', [
    { active: true, key: 'CHEAP-CLICK' },
    { active: true, key: 'HIGH-CLICK-VALUE' },
    { active: true, key: 'HIGH-CTR' },
    { active: true, key: 'HIGH-VOLUME' },
    { active: true, key: 'LOW-CLICK-VALUE' },
    { active: true, key: 'ONE-CONVERSION' },
  ]),
  criterionName: {
    key: 'name',
    columnName: 'criterion_name',
    displayName: '',
    type: 'query',
    apiRefinerType: 'search',
    queryKeyword: 'contains',
    value: null,
  },
};

export const rulesFilters = {
  ruleAction: multiSelectFilter('ruleAction', 'rule_action', [
    { active: true, key: 'ADD' },
    { active: true, key: 'REVIEW' },
    { active: true, key: 'REJECT' },
  ]),
  ruleInherited: multiSelectFilter('ruleInherited', 'rule_inherited', [
    { active: true, key: 'true' },
    { active: true, key: 'false' },
  ]),
  ruleTargetingType: createMultiSelectFilterFromEnum(CampaignTargetingType, 'ruleTargetingType'),
  ruleMatchType: createMultiSelectFilterFromEnum(CriterionAdditionMatchType, 'ruleMatchType'),
  ruleNegative: multiSelectFilter('ruleNegative', 'rule_negative', [
    { active: true, key: 'true' },
    { active: true, key: 'false' },
  ]),
  ruleProductAdSyncMode: createMultiSelectFilterFromEnum(ProductAdSyncMode, 'ruleProductAdSyncMode'),
};

export const accountFilters = {
  campaignOptimize: getOptimizeFilter(),
  platform: multiSelectFilter('platform', 'platform', [
    { active: true, text: 'Amazon', key: 'AMAZON' },
    { active: true, text: 'eBay', key: 'EBAY' },
    { active: true, text: 'Google', key: 'GOOGLE' },
    { active: true, text: 'Microsoft', key: 'MICROSOFT' },
    { active: true, text: 'Walmart', key: 'WALMART' },
  ]),
  tags: {
    key: 'tags',
    columnName: 'tags_user',
    displayName: '',
    apiRefinerType: 'filter',
    type: 'multi-select',
    options: [],
    maxSelection: 5,
    optionsUri: { api: ApiEndpoint.Accounts, route: ApiCommonRoutes.Tags },
    autocomplete: true,
    value: null,
  },
};

export const PGFilters = {
  enabled: getOptimizeFilter('enabled'),
  optiType: multiSelectFilter('optiType', 'optimization_type', [
    { active: true, key: 'CLICK' },
    { active: true, key: 'PROFIT' },
    { active: true, key: 'REVENUE' },
    { active: true, key: 'VCPM' },
  ]),
  goalType: multiSelectFilter('goalType', 'goal_types', [
    { active: true, key: 'CLICKS-PER-DAY' },
    { active: true, key: 'COST-PER-CLICK' },
    { active: true, key: 'COST-PER-CONVERSION' },
    { active: true, key: 'COST-PER-DAY' },
    { active: true, key: 'CRR' },
    { active: true, key: 'PROFIT-MAXIMIZATION' },
    { active: true, key: 'ROAS' },
    { active: true, key: 'ROI' },
  ]),
  campaignsCount: multiSelectFilter('campaignsCount', 'campaigns_count', [
    { active: true, key: 'EMPTY' },
    { active: true, key: 'ACTIVE' },
  ]),
};

const campaignTypeOptions: SubFilterOption[] = [
  { platform: platforms.AMAZON.name, active: true, text: 'Amazon Sponsored Brands', key: 'AMAZON-SPONSORED-BRANDS' },
  { platform: platforms.AMAZON.name, active: true, text: 'Amazon Sponsored Display', key: 'AMAZON-SPONSORED-DISPLAY' },
  {
    platform: platforms.AMAZON.name,
    active: true,
    text: 'Amazon Sponsored Products',
    key: 'AMAZON-SPONSORED-PRODUCTS',
  },
  { platform: platforms.GOOGLE.name, active: true, text: 'Google Display', key: 'GOOGLE-DISPLAY' },
  { platform: platforms.GOOGLE.name, active: true, text: 'Google Express', key: 'GOOGLE-EXPRESS' },
  { platform: platforms.GOOGLE.name, active: true, text: 'Google Search', key: 'GOOGLE-SEARCH' },
  { platform: platforms.GOOGLE.name, active: true, text: 'Google Shopping', key: 'GOOGLE-SHOPPING' },
  { platform: platforms.GOOGLE.name, active: true, text: 'Google Universal App', key: 'GOOGLE-UNIVERSAL-APP' },
  { platform: platforms.GOOGLE.name, active: true, text: 'Google Unknown', key: 'GOOGLE-UNKNOWN' },
  { platform: platforms.GOOGLE.name, active: true, text: 'Google Video', key: 'GOOGLE-VIDEO' },
  {
    platform: platforms.EBAY.name,
    active: true,
    text: 'eBay Promoted Listings Priority',
    key: 'EBAY-PROMOTED-LISTINGS-ADVANCED',
  },
  {
    platform: platforms.EBAY.name,
    active: true,
    text: 'eBay Promoted Listings General',
    key: 'EBAY-PROMOTED-LISTINGS-STANDARD',
  },
  { platform: platforms.MICROSOFT.name, active: true, text: 'Microsoft Audience', key: 'MICROSOFT-AUDIENCE' },
  {
    platform: platforms.MICROSOFT.name,
    active: true,
    text: 'Microsoft Dynamic Search Ads',
    key: 'MICROSOFT-DYNAMIC-SEARCH-ADS',
  },
  { platform: platforms.MICROSOFT.name, active: true, text: 'Microsoft Search', key: 'MICROSOFT-SEARCH' },
  { platform: platforms.MICROSOFT.name, active: true, text: 'Microsoft Shopping', key: 'MICROSOFT-SHOPPING' },
  { platform: platforms.WALMART.name, active: true, text: 'Walmart Sponsored Brands', key: 'WALMART-SPONSORED-BRANDS' },
  {
    platform: platforms.WALMART.name,
    active: true,
    text: 'Walmart Sponsored Products',
    key: 'WALMART-SPONSORED-PRODUCTS',
  },
  { platform: platforms.WALMART.name, active: true, text: 'Walmart Sponsored Video', key: 'WALMART-SPONSORED-VIDEO' },
];

export const campaignFilters = {
  status: multiSelectFilter('status', 'campaign_status', [
    { active: true, key: 'ACTIVE' },
    { active: true, key: 'DELETED' },
    { active: true, key: 'PAUSED' },
  ]),
  ras: multiSelectFilter('ras', 'campaign_amz_ras_campaign', [
    { active: true, key: 'true' },
    { active: true, key: 'false' },
  ]),
  type: multiSelectFilter('type', 'campaign_type', campaignTypeOptions),
  optiType: multiSelectFilter('optiType', 'campaign_optimization_type', [
    { active: true, key: 'CLICK' },
    { active: true, key: 'PROFIT' },
    { active: true, key: 'REVENUE' },
    { active: true, key: 'VCPM' },
  ]),
  name: {
    key: 'name',
    columnName: 'campaign_name',
    displayName: '',
    type: 'query',
    apiRefinerType: 'search',
    queryKeyword: 'contains',
    value: null,
  },
  campaignOptimize: multiSelectFilter('optimize', 'campaign_optimize', [
    { active: true, key: 'true' },
    { active: true, key: 'false' },
  ]),
  portfolio: {
    key: 'portfolio',
    columnName: 'adspert_portfolio_id',
    displayName: '',
    apiRefinerType: 'filter',
    type: 'multi-select',
    options: [],
    maxSelection: 5,
    customTemplate: true,
    optionsUri: { api: ApiEndpoint.Customers, route: ApiCommonRoutes.Portfolios },
    autocomplete: true,
    value: null,
  },
  label: {
    key: 'label',
    columnName: 'campaign_labels',
    displayName: '',
    apiRefinerType: 'filter',
    type: 'multi-select',
    options: [],
    maxSelection: 5,
    customTemplate: true,
    optionsUri: { api: ApiEndpoint.Preview, route: ApiCommonRoutes.Labels },
    autocomplete: true,
    value: null,
  },
};

export const adGroupFilters = {
  status: multiSelectFilter('status', 'adgroup_status', [
    { active: true, text: 'ACTIVE', key: 'ACTIVE' },
    { active: true, text: 'DELETED', key: 'DELETED' },
    { active: true, text: 'PAUSED', key: 'PAUSED' },
  ]),
  name: {
    key: 'name',
    columnName: 'adgroup_name',
    displayName: '',
    type: 'query',
    apiRefinerType: 'search',
    queryKeyword: 'contains',
    value: null,
  },
  retailers: <SubFilter>{
    key: 'retailers',
    columnName: 'adgroup_amz_retailer_id',
    displayName: '',
    apiRefinerType: 'filter',
    type: 'multi-select',
    options: [],
    maxSelection: 5,
    optionsUri: { api: ApiEndpoint.RAS, route: ApiCommonRoutes.Retailers },
    autocomplete: true,
    value: null,
  },
};

function createMultiSelectFilterFromEnum<T extends object>(source: T, key: string): SubFilter {
  return multiSelectFilter(
    key,
    camelToSnake(key),
    Object.values(source).map((sourceKey) => ({
      active: true,
      text: sourceKey,
      key: sourceKey,
    })),
  );
}

export const destinationAdGroupFilters = {
  adgroupName: { ...adGroupFilters.name, key: 'adgroup_name' },
  adgroupStatus: { ...adGroupFilters.status, key: 'adgroup_status' },
  amzProductAdsMatchRatio: {
    key: 'amz_product_ads_match_ratio',
    columnName: 'amz_product_ads_match_ratio',
    displayName: '',
    type: 'compare',
    apiRefinerType: 'filter',
    subType: MetricType.percent,
    currency: null,
    comparator: CompareValues.GE,
    enabledComparators: [CompareValues.GE, CompareValues.LE],
    value: null,
  },
  campaignName: { ...campaignFilters.name, key: 'campaign_name' },
  campaignOptimize: { ...campaignFilters.campaignOptimize, key: 'campaign_optimize' },
  campaignStatus: { ...campaignFilters.status, key: 'campaign_status' },
  campaignType: { ...campaignFilters.type, key: 'campaign_type' },
  performanceGroupName: {
    key: 'performance_group_name',
    columnName: 'performance_group_name',
    displayName: '',
    type: 'query',
    apiRefinerType: 'search',
    queryKeyword: 'contains',
    value: null,
  },
  performanceGroupEnabled: getOptimizeFilter('performance_group_enabled'),
};

export const criteriaFilters = {
  status: multiSelectFilter('status', 'criterion_status', [
    { active: true, text: 'ACTIVE', key: 'ACTIVE' },
    { active: true, text: 'DELETED', key: 'DELETED' },
    { active: true, text: 'PAUSED', key: 'PAUSED' },
  ]),
  matchType: multiSelectFilter('matchType', 'criterion_match_type', [
    { active: true, text: 'BROAD', key: 'BROAD' },
    { active: true, text: 'EXACT', key: 'EXACT' },
    { active: true, text: 'PHRASE', key: 'PHRASE' },
  ]),
  type: multiSelectFilter('type', 'criterion_type', [
    { active: true, text: 'KEYWORD', key: 'KEYWORD' },
    { active: true, text: 'PLACEMENT', key: 'PLACEMENT' },
    { active: true, text: 'WEBPAGE', key: 'WEBPAGE' },
    { active: true, text: 'PAT', key: 'PAT' },
    { active: true, text: 'MOBILE-APP', key: 'MOBILE-APP' },
    { active: true, text: 'MOBILE-APP-CATEGORY', key: 'MOBILE-APP-CATEGORY' },
    { active: true, text: 'YOUTUBE-CHANNEL', key: 'YOUTUBE-CHANNEL' },
    { active: true, text: 'YOUTUBE-VIDEO', key: 'YOUTUBE-VIDEO' },
    { active: true, text: 'PRODUCT-PARTITION', key: 'PRODUCT-PARTITION' },
    { active: true, text: 'DSA-AUTO-TARGET', key: 'DSA-AUTO-TARGET' },
    { active: true, text: 'AUTOTARGETING', key: 'AUTOTARGETING' },
    { active: true, text: 'RETARGETING', key: 'RETARGETING' },
    { active: true, text: 'WEBPAGE-FILTER', key: 'WEBPAGE-FILTER' },
    { active: true, text: 'AUDIENCES', key: 'AUDIENCES' },
    { active: true, text: 'VIEWS-REMARKETING', key: 'VIEWS-REMARKETING' },
    { active: true, text: 'FEED-FILTER', key: 'FEED-FILTER' },
  ]),
  name: {
    key: 'name',
    columnName: 'criterion_name',
    displayName: '',
    type: 'query',
    apiRefinerType: 'search',
    queryKeyword: 'contains',
    value: null,
  },
};

export const campaignsPGFilters = {
  campaignOptimize: multiSelectFilter('optimize', 'campaign_optimize', [
    { active: true, key: 'true' },
    { active: true, key: 'false' },
  ]),
  type: multiSelectFilter('type', 'campaign_type', campaignTypeOptions),
  status: multiSelectFilter('status', 'campaign_status', [
    { active: true, key: 'ACTIVE' },
    { active: true, key: 'DELETED' },
    { active: true, key: 'PAUSED' },
  ]),
  platform: multiSelectFilter('platform', 'account_platform', [
    { active: true, text: 'Amazon', key: 'AMAZON' },
    { active: true, text: 'Google', key: 'GOOGLE' },
    { active: true, text: 'Microsoft', key: 'MICROSOFT' },
  ]),
  tags: {
    key: 'tags',
    columnName: 'account_tags_user',
    displayName: '',
    apiRefinerType: 'filter',
    type: 'multi-select',
    options: [],
    maxSelection: 5,
    optionsUri: { api: ApiEndpoint.Accounts, route: ApiCommonRoutes.Tags },
    autocomplete: true,
    value: null,
  },
  accounts: {
    key: 'accounts',
    columnName: 'adspert_account_id',
    displayName: '',
    apiRefinerType: 'filter',
    type: 'multi-select',
    options: [],
    maxSelection: 5,
    customTemplate: true,
    optionsUri: { api: ApiEndpoint.Accounts, route: '' },
    autocomplete: true,
    value: null,
  },
  pg: {
    key: 'pg',
    columnName: 'performance_group_id',
    displayName: '',
    apiRefinerType: 'filter',
    type: 'multi-select',
    options: [],
    maxSelection: 5,
    customTemplate: true,
    optionsUri: { api: ApiEndpoint.Customers, route: ApiCommonRoutes.PerformanceGroups },
    autocomplete: true,
    value: null,
  },
  portfolio: {
    key: 'portfolio',
    columnName: 'adspert_portfolio_id',
    displayName: '',
    apiRefinerType: 'filter',
    type: 'multi-select',
    options: [],
    maxSelection: 5,
    customTemplate: true,
    optionsUri: { api: ApiEndpoint.Customers, route: ApiCommonRoutes.Portfolios },
    autocomplete: true,
    value: null,
  },
  label: {
    key: 'label',
    columnName: 'campaign_labels',
    displayName: '',
    apiRefinerType: 'filter',
    type: 'multi-select',
    options: [],
    maxSelection: 5,
    customTemplate: true,
    optionsUri: { api: ApiEndpoint.Preview, route: ApiCommonRoutes.Labels },
    autocomplete: true,
    value: null,
  },
  ...campaignMetricsFilters,
};

export const goalsPGFilters = {
  enabled: getOptimizeFilter('performance_group_enabled'),
  platform: multiSelectFilter('platform', 'account_platform', [
    { active: true, text: 'Amazon', key: 'AMAZON' },
    { active: true, text: 'Google', key: 'GOOGLE' },
    { active: true, text: 'Microsoft', key: 'MICROSOFT' },
  ]),
  accounts: {
    key: 'accounts',
    columnName: 'adspert_account_id',
    displayName: '',
    apiRefinerType: 'filter',
    type: 'multi-select',
    options: [],
    maxSelection: 5,
    customTemplate: true,
    optionsUri: { api: ApiEndpoint.Accounts, route: '' },
    autocomplete: true,
    value: null,
  },
  campaignsCount: multiSelectFilter('campaignsCount', 'campaigns_count', [
    { active: true, key: 'EMPTY' },
    { active: true, key: 'ACTIVE' },
  ]),
};

export const BudgetSuggestionsFilters = {
  ...MetricsFilters,
  [Metrics.BUDGET_ESTD_OPTIMAL_SPENDING]: createCompareMetric(
    Metrics.BUDGET_ESTD_OPTIMAL_SPENDING,
    MetricType.currency,
  ),
  [Metrics.AVG_COST_7]: createCompareMetric(Metrics.AVG_COST_7, MetricType.currency),
  [Metrics.AVG_COVERAGE_7]: createCompareMetric(Metrics.AVG_COVERAGE_7, MetricType.percent),
  [Metrics.BUDGET_USAGE_7]: createCompareMetric(Metrics.BUDGET_USAGE_7, MetricType.percent),
  [Metrics.VIEW_IMPRESSIONS]: createCompareMetric(Metrics.VIEW_IMPRESSIONS, MetricType.number),
  [Metrics.COST_PER_MILLE]: createCompareMetric(Metrics.COST_PER_MILLE, MetricType.currency),
};

export const BudgetQueueFilters = {
  [Metrics.BUDGET_SUGGESTED_ACCOUNT_CURRENCY_VALUE]: createCompareMetric(
    Metrics.BUDGET_SUGGESTED_ACCOUNT_CURRENCY_VALUE,
    MetricType.currency,
  ),
  [Metrics.BUDGET_APPROVED_ACCOUNT_CURRENCY_VALUE]: createCompareMetric(
    Metrics.BUDGET_APPROVED_ACCOUNT_CURRENCY_VALUE,
    MetricType.currency,
  ),
  [Metrics.BUDGET_CURRENT_ACCOUNT_CURRENCY_VALUE]: createCompareMetric(
    Metrics.BUDGET_CURRENT_ACCOUNT_CURRENCY_VALUE,
    MetricType.currency,
  ),
};

export const CampaignCreationPGFilters = {
  enabled: getOptimizeFilter('performance_group_enabled'),
  pgName: {
    key: 'name',
    columnName: 'performance_group_name',
    displayName: '',
    type: 'query',
    apiRefinerType: 'search',
    queryKeyword: 'contains',
    value: null,
  },
  goalType: multiSelectFilter('goalType', 'performance_group_goal_types', [
    { active: true, key: 'CLICKS-PER-DAY' },
    { active: true, key: 'COST-PER-CLICK' },
    { active: true, key: 'COST-PER-CONVERSION' },
    { active: true, key: 'COST-PER-DAY' },
    { active: true, key: 'CRR' },
    { active: true, key: 'PROFIT-MAXIMIZATION' },
    { active: true, key: 'ROAS' },
    { active: true, key: 'ROI' },
  ]),
};
